<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading…</div>
    <div v-if="loaded" class="root">

      <!-- Diagonal warm band -->
      <div class="bg-band"></div>

      <!-- Nav -->
      <nav class="nav">
        <div class="nav-left">
          <img v-if="loinfo && loinfo.main_logo" :src="loinfo.main_logo" class="nav-logo" />
          <div class="nav-brand">
            <span class="nav-lo">{{ loName || "Loan Officer" }}</span>
            <span class="nav-sep"> with </span>
            <span class="nav-re">{{ realtorName || realtor?.partner_title || 'Partner' }}</span>
          </div>
        </div>
     
      </nav>

      <!-- Hero -->
      <section class="hero">
        <div class="hero-text">
 
          <h1 class="hero-h1" v-html="heroH1Html"></h1>
          <p class="hero-body">{{ heroBody }}</p>
          <div class="hero-actions">
  
            <a v-if="applyLeftLink" class="btn-soft" :href="applyLeftLink">{{ heroSecondaryText }}</a>
          </div>
          <div class="hero-quick">
            <a v-if="loinfo && loinfo.phone_number" class="hq-link" :href="'tel:' + loinfo.phone_number">Call us</a>
            <span class="hq-dot">·</span>
            <a v-if="loinfo && loinfo.email" class="hq-link" :href="'mailto:' + loinfo.email">Send an email</a>
            <span v-if="realtor && realtor.website_url" class="hq-dot">·</span>
            <a v-if="realtor && realtor.website_url" class="hq-link" :href="realtor.website_url" target="_blank" rel="noreferrer">Partner site</a>
          </div>
        </div>

        <!-- Floating profile cluster -->
        <div class="hero-profiles">
          <div class="profile-cluster">

            <div class="pcard lo-card">
              <div class="pcard-top">
                <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="pcard-avatar" />
                <div class="pcard-tag-bar">
                  <span class="pcard-tag">Loan Officer</span>
                </div>
              </div>
              <div class="pcard-name">{{ loName }}</div>
              <div class="pcard-role">{{ loTitle }}</div>
              <div v-if="loinfo && loinfo.nmls" class="pcard-nmls">NMLS {{ (loinfo.nmls || "").trim() }}</div>
              <div class="pcard-contacts">
                <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="pcard-link">{{ loinfo.phone_number }}</a>
                <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="pcard-link">{{ loinfo.email }}</a>
              </div>
              <a class="pcard-btn" :href="applyRightLink">{{ heroPrimaryText }}</a>
            </div>

            <div class="connector-line">
              <div class="connector-dot"></div>
              <div class="connector-track"></div>
              <div class="connector-dot"></div>
            </div>

            <div class="pcard re-card">
              <div class="pcard-top">
                <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="pcard-avatar" />
                <div class="pcard-tag-bar re-tag-bar">
                  <span class="pcard-tag">{{ realtor?.partner_title || 'Partner' }}</span>
                </div>
              </div>
              <div class="pcard-name">{{ realtorName }}</div>
              <div class="pcard-role">{{ realtor && realtor.brokerage_name ? realtor.brokerage_name : "" }}</div>
              <div v-if="realtor && realtor.nmls" class="pcard-nmls">NMLS {{ (realtor.nmls || "").trim() }}</div>
              <div class="pcard-contacts">
                <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="pcard-link re-link">{{ realtor.phone_number }}</a>
                <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="pcard-link re-link">{{ realtor.email }}</a>
                <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="pcard-link re-link" target="_blank" rel="noreferrer">Website →</a>
              </div>
            </div>

          </div>
        </div>
      </section>

      <!-- Footer warm strip -->
      <footer class="footer">
        <span class="footer-text">Ready to get started? <a :href="applyRightLink" class="footer-link">{{ heroPrimaryText }} →</a></span>
      </footer>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending: { primary: "#eb610a", accent: "#646363" },
  radiantmortgage: { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage: { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam: { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage: { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage: { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial: { primary: "#0b1726", accent: "#c69c39" },
};
function getBrandKey(email) {
  if (!email) return null;
  const d = email.slice(email.indexOf("@") + 1).toLowerCase();
  return d.split(".")[0] || null;
}
export default {
  name: "CoBrandHearthside",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Ready to get<br/><em>started?</em>";
      const parts = h.split(",");
      if (parts.length > 1) {
        const a = parts[0].trim();
        const b = parts.slice(1).join(",").trim();
        return `${a}<br/><em>${b}</em>`;
      }
      return h;
    },
    
    heroHeading() { return this.loinfo?.text_section_heading || "About"; },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule Appointment"; },
    heroBody() {
      const p = [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim();
      return p || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing with a simple, personal process.";
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,600;1,400;1,600&family=Plus+Jakarta+Sans:wght@300;400;500;600&display=swap');

.page {
  min-height: 100vh;
  background: #faf7f2;
  color: #2c2420;
  font-family: 'Plus Jakarta Sans', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --warm-bg: #faf7f2;
  --card-bg: #ffffff;
  --warm-mid: #f0e9df;
  --text-muted: #8a7b70;
  --a-warm: color-mix(in srgb, var(--a) 15%, #faf7f2);
  --p-warm: color-mix(in srgb, var(--p) 8%, #faf7f2);
  position: relative;
  overflow-x: hidden;
}

.root { position: relative; z-index: 1; min-height: 100vh; display: flex; flex-direction: column; }

/* DIAGONAL BAND — large warm swoosh crossing the page */
.bg-band {
  position: fixed;
  top: -20%;
  left: -10%;
  width: 130%;
  height: 65%;
  background: linear-gradient(160deg,
    color-mix(in srgb, var(--a) 12%, #faf7f2) 0%,
    color-mix(in srgb, var(--a) 7%, #faf7f2) 40%,
    transparent 70%);
  transform: rotate(-6deg);
  pointer-events: none;
  z-index: 0;
}

/* NAV */
.nav {
  position: relative; z-index: 10;
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 40px;
  border-bottom: 1px solid color-mix(in srgb, var(--a) 18%, transparent);
  background: rgba(250, 247, 242, 0.85);
  backdrop-filter: blur(12px);
}
.nav-left { display: flex; align-items: center; gap: 16px; }
.nav-logo { width: 160px; max-height:80px; object-fit: contain; }
.nav-brand { font-size: 13px; }
.nav-lo { font-weight: 600; color: var(--p); }
.nav-sep { color: var(--text-muted); padding: 0 2px; }
.nav-re { color: var(--text-muted); }
.nav-cta {
  font-size: 13px; font-weight: 600; text-decoration: none;
  background: var(--p); color: #faf7f2;
  padding: 10px 22px; border-radius: 50px;
  transition: opacity 0.2s;
}
.nav-cta:hover { opacity: 0.85; }

/* HERO */
.hero {
  flex: 1; display: grid;
  grid-template-columns: 1fr 480px;
  gap: 40px; align-items: center;
  padding: 64px 40px;
  max-width: 1160px; margin: 0 auto; width: 100%;
  position: relative; z-index: 1;
}

.hero-eyebrow {
  font-size: 12px; font-weight: 500; letter-spacing: 0.18em;
  text-transform: uppercase; color: var(--a);
  margin-bottom: 14px;
}
.hero-h1 {
  font-family: 'Lora', serif;
  font-size: clamp(34px, 4.5vw, 58px);
  font-weight: 600; line-height: 1.15;
  color: var(--p); margin: 0 0 18px;
}
.hero-h1 em { font-style: italic; color: color-mix(in srgb, var(--a) 80%, var(--p)); }
.hero-body { font-size: 15px; line-height: 1.75; color: #5a4e47; margin-bottom: 28px; }
.hero-actions { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 20px; justify-content: center; }
.btn-warm {
  font-size: 14px; font-weight: 600; text-decoration: none;
  background: var(--p); color: #faf7f2;
  padding: 13px 26px; border-radius: 50px;
  box-shadow: 0 4px 18px color-mix(in srgb, var(--p) 25%, transparent);
  transition: transform 0.2s, box-shadow 0.2s;
   width:55%
}
.btn-warm:hover { transform: translateY(-1px); box-shadow: 0 8px 24px color-mix(in srgb, var(--p) 30%, transparent); }
.btn-soft {
  font-size: 14px; font-weight: 500; text-decoration: none;
  background: var(--warm-mid); color: var(--p);
  padding: 13px 26px; border-radius: 50px;
  transition: background 0.2s;
}
.btn-soft:hover { background: color-mix(in srgb, var(--a) 12%, var(--warm-mid)); }
.hero-quick { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.hq-link { font-size: 13px; text-decoration: none; color: var(--text-muted); border-bottom: 1px solid color-mix(in srgb, var(--a) 35%, transparent); padding-bottom: 1px; transition: color 0.2s; }
.hq-link:hover { color: color-mix(in srgb, var(--a) 80%, var(--p)); }
.hq-dot { color: var(--text-muted); }

/* PROFILE CLUSTER */
.profile-cluster { display: flex; flex-direction: column; gap: 0; }

.pcard {
  background: var(--card-bg);
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 8px 32px rgba(44, 36, 32, 0.10), 0 2px 8px rgba(44, 36, 32, 0.05);
  border: 1px solid color-mix(in srgb, var(--a) 14%, transparent);
  position: relative;
  overflow: hidden;
}
.pcard::before {
  content: "";
  position: absolute; top: 0; left: 0; right: 0; height: 3px;
  background: linear-gradient(90deg, var(--p), color-mix(in srgb, var(--p) 40%, var(--a)));
}
.re-card::before {
  background: linear-gradient(90deg, var(--a), color-mix(in srgb, var(--a) 50%, var(--p)));
}
.pcard-top { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.pcard-avatar { width: 52px; border-radius: 14px; object-fit: cover; }

.pcard-tag { font-size: 10px; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; color: color-mix(in srgb, var(--p) 60%, var(--text-muted)); }
.re-tag-bar .pcard-tag { color: color-mix(in srgb, var(--a) 80%, var(--p)); }
.pcard-name { font-family: 'Lora', serif; font-size: 19px; font-weight: 600; color: var(--p); }
.pcard-role { font-size: 12px; color: var(--text-muted); margin-top: 2px; }
.pcard-nmls { font-size: 11px; color: color-mix(in srgb, var(--text-muted) 70%, transparent); margin-top: 2px; }
.pcard-contacts { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }
.pcard-link {
  font-size: 11px; font-weight: 500; text-decoration: none;
  background: var(--p-warm); color: var(--p);
  padding: 5px 12px; border-radius: 50px;
  border: 1px solid color-mix(in srgb, var(--p) 10%, transparent);
  transition: background 0.18s;
}
.pcard-link:hover { background: color-mix(in srgb, var(--p) 12%, var(--warm-bg)); }
.re-link { background: var(--a-warm); color: color-mix(in srgb, var(--a) 85%, var(--p)); border-color: color-mix(in srgb, var(--a) 18%, transparent); }
.re-link:hover { background: color-mix(in srgb, var(--a) 20%, var(--warm-bg)); }
.pcard-btn {
  display: block; text-align: center; text-decoration: none;
  background: var(--p); color: #faf7f2;
  font-size: 13px; font-weight: 600;
  padding: 11px; border-radius: 12px;
  margin-top: 14px; transition: opacity 0.2s;
}
.pcard-btn:hover { opacity: 0.85; }

/* CONNECTOR */
.connector-line {
  display: flex; flex-direction: column; align-items: center; gap: 0;
  height: 28px; position: relative; z-index: 2;
}
.connector-dot { width: 8px; height: 8px; border-radius: 999px; background: color-mix(in srgb, var(--a) 60%, white); }
.connector-track { width: 2px; flex: 1; background: linear-gradient(180deg, color-mix(in srgb, var(--a) 50%, transparent), color-mix(in srgb, var(--a) 50%, transparent)); }

/* FOOTER */
.footer {
  padding: 18px 40px; text-align: center;
  border-top: 1px solid color-mix(in srgb, var(--a) 14%, transparent);
  background: color-mix(in srgb, var(--a) 6%, #faf7f2);
}
.footer-text { font-size: 13px; color: var(--text-muted); }
.footer-link { font-weight: 600; text-decoration: none; color: var(--p); border-bottom: 1px solid color-mix(in srgb, var(--p) 25%, transparent); }
.footer-link:hover { color: color-mix(in srgb, var(--a) 80%, var(--p)); }

@media (max-width: 820px) {
  .hero { grid-template-columns: 1fr; padding: 40px 24px; }
  .nav { padding: 16px 24px; }
}
</style>
